/**
 * Relative-range tokens -> concrete date windows, and prior-period arithmetic.
 *
 * ## Why this exists (TV1-1930 · S15 — prefactor)
 *
 * `DateRange.tsx` emits a token (`'15m' | '1h' | '1d' | '1w' | '2w' | '1mo' |
 * '3mo' | '6mo' | '1y'`) and `widgetDefinition.ts` used to assign that token
 * **verbatim** to `timeDimension.dateRange`. **Cube cannot parse `'1mo'`.**
 *
 * Verified against the shipped parser, not assumed —
 * `@cubejs-backend/api-gateway/dist/src/date-parser.js` matches only
 * `(this|last|next) <unit>`, `(last|next) <N> <unit>`, `today` / `yesterday` /
 * `tomorrow`, and `from X to Y`; everything else falls through to
 * `chrono-node`, which either throws `Can't parse date: '1mo'` or — worse —
 * parses it to a single instant, yielding a **zero-width window** and a silently
 * empty widget. So every date-ranged widget built through the UI has been
 * sending Cube a range it cannot honour. This is a pre-existing latent bug, and
 * a hard prerequisite for the KPI's prior-period compare: you cannot subtract a
 * period you never resolved.
 *
 * ## Semantics (pinned in `__tests__/dateRange.test.ts`)
 *
 * - `resolveRelativeRange` produces a **rolling window ending now**:
 *   `[ now - duration, now ]`. Minute/hour/day/week tokens use exact durations;
 *   month/year tokens use **calendar** arithmetic (so "Last month" really is one
 *   month back, not 30 days), with **month-end clamping** — `Mar 31 - 1mo` is
 *   `Feb 28`, never the `Mar 3` that naive `setMonth` produces.
 *
 * - `priorPeriodOf` is the **immediately preceding window of identical length**:
 *   `[ start - span, start ]`. Contiguous, no gap, no overlap. This is the only
 *   definition that is also well-defined for a *custom* (explicit start/end)
 *   range, which is why it takes a resolved window rather than a token.
 *
 * - Unknown / non-token strings are **NOT** resolved here — see
 *   `resolveDateRangeWindow` in `widgetDefinition.ts`, which passes them to Cube
 *   verbatim so that anything Cube's own parser already understands ("last 30
 *   days") keeps working exactly as it does today.
 */

/** The nine tokens the builder's Relative Range select can emit. */
export type RelativeRangeToken = '15m' | '1h' | '1d' | '1w' | '2w' | '1mo' | '3mo' | '6mo' | '1y';

/** A concrete, absolute window: `[ startISO, endISO ]`. */
export type ResolvedDateRange = [ string, string ];

type Duration = { amount: number; unit: 'minute' | 'hour' | 'day' | 'month' | 'year' };

/**
 * Token -> duration. Weeks are expressed in days (a week is exactly 7 days);
 * months and years stay calendar units on purpose — see the module docblock.
 */
const TOKEN_DURATIONS: Record<RelativeRangeToken, Duration> = {
  '15m': { amount: 15, unit: 'minute' },
  '1h': { amount: 1, unit: 'hour' },
  '1d': { amount: 1, unit: 'day' },
  '1w': { amount: 7, unit: 'day' },
  '2w': { amount: 14, unit: 'day' },
  '1mo': { amount: 1, unit: 'month' },
  '3mo': { amount: 3, unit: 'month' },
  '6mo': { amount: 6, unit: 'month' },
  '1y': { amount: 12, unit: 'month' }
};

/** `true` for one of the nine tokens the builder actually emits. */
export const isRelativeRangeToken = ( value: unknown ): value is RelativeRangeToken =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call( TOKEN_DURATIONS, value );

/**
 * Subtract whole calendar months, **clamping to the end of the target month**.
 *
 * The naive `d.setUTCMonth( d.getUTCMonth() - n )` overflows: on `Mar 31`,
 * asking for February yields "Feb 31", which JS rolls forward to `Mar 3` — a
 * window that ends BEFORE it starts on one day in twelve. Shifting from the 1st
 * and then clamping the day is the fix.
 */
const subtractMonths = ( date: Date, months: number ): Date => {
  const dayOfMonth = date.getUTCDate();
  const shifted = new Date( date.getTime());

  shifted.setUTCDate( 1 );
  shifted.setUTCMonth( shifted.getUTCMonth() - months );

  // Day 0 of the NEXT month is the last day of this one.
  const lastDayOfTargetMonth = new Date(
    Date.UTC( shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0 )
  ).getUTCDate();

  shifted.setUTCDate( Math.min( dayOfMonth, lastDayOfTargetMonth ));
  return shifted;
};

const subtract = ( date: Date, { amount, unit }: Duration ): Date => {
  const shifted = new Date( date.getTime());

  switch ( unit ) {
    case 'minute':
      shifted.setUTCMinutes( shifted.getUTCMinutes() - amount );
      return shifted;
    case 'hour':
      shifted.setUTCHours( shifted.getUTCHours() - amount );
      return shifted;
    case 'day':
      shifted.setUTCDate( shifted.getUTCDate() - amount );
      return shifted;
    case 'month':
    case 'year':
      return subtractMonths( date, amount );
  }
};

/**
 * Resolve one of the nine builder tokens into a concrete `[ start, end ]` window
 * ending at `now`.
 *
 * Returns `null` — never a guess — for anything that is not a known token. The
 * caller decides what to do with an unresolvable range (see
 * `resolveDateRangeWindow`); silently inventing a window would be worse than
 * showing none.
 *
 * ISO-8601 with an explicit `Z`, matching what the builder's *custom* range
 * already sends (`DateRange.tsx` -> `from.toISOString()`), so both paths hand
 * Cube the same shape. Full timestamps (not `YYYY-MM-DD`) because the `'15m'`
 * and `'1h'` tokens are meaningless at day precision.
 */
export const resolveRelativeRange = (
  token: unknown,
  now: Date = new Date()
): ResolvedDateRange | null => {
  if ( !isRelativeRangeToken( token )) return null;

  const start = subtract( now, TOKEN_DURATIONS[token]);
  return [ start.toISOString(), now.toISOString() ];
};

/**
 * The window immediately preceding `range`, of exactly the same length.
 *
 * `[ start - span, start ]`. Contiguous with the current window (its end IS the
 * current start), so no day is counted twice and none falls in a gap.
 *
 * Returns `null` for a degenerate window (zero-width or inverted). A KPI must
 * then report its delta as **unknown**, not as zero — you cannot compare against
 * a period of no duration.
 */
export const priorPeriodOf = ( range: ResolvedDateRange | null | undefined ): ResolvedDateRange | null => {
  if ( !Array.isArray( range ) || range.length !== 2 ) return null;

  const startMs = Date.parse( range[0]);
  const endMs = Date.parse( range[1]);
  if ( Number.isNaN( startMs ) || Number.isNaN( endMs )) return null;

  const span = endMs - startMs;
  if ( span <= 0 ) return null;

  return [ new Date( startMs - span ).toISOString(), new Date( startMs ).toISOString() ];
};
